import React from 'react';

type OrientationEventWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

const clamp = (value: number) => Math.max(-0.5, Math.min(0.5, value));

/** Smoothly exposes pointer/tilt input as --px and --py on a decorative container. */
export function useParallaxInput<T extends HTMLElement>() {
  const ref = React.useRef<T>(null);

  React.useEffect(() => {
    const element = ref.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!element || reducedMotion.matches) return;

    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;
    let frame = 0;
    const Orientation = window.DeviceOrientationEvent as OrientationEventWithPermission | undefined;
    // Never prompt for sensor access for a decorative effect. Platforms that
    // require explicit permission (notably iOS) retain pointer-only parallax.
    const orientationEnabled = Boolean(Orientation && !Orientation.requestPermission);

    const scheduleRender = () => {
      if (!frame) frame = requestAnimationFrame(render);
    };

    const render = () => {
      frame = 0;
      currentX += (targetX - currentX) * 0.1;
      currentY += (targetY - currentY) * 0.1;

      const settled = Math.abs(targetX - currentX) < 0.0005
        && Math.abs(targetY - currentY) < 0.0005;
      if (settled) {
        currentX = targetX;
        currentY = targetY;
      }

      element.style.setProperty('--px', currentX.toFixed(4));
      element.style.setProperty('--py', currentY.toFixed(4));
      if (!settled) scheduleRender();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return;
      const bounds = element.getBoundingClientRect();
      targetX = clamp((event.clientX - bounds.left) / bounds.width - 0.5);
      targetY = clamp((event.clientY - bounds.top) / bounds.height - 0.5);
      scheduleRender();
    };
    const resetPointer = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') {
        targetX = targetY = 0;
        scheduleRender();
      }
    };
    const onOrientation = (event: DeviceOrientationEvent) => {
      if (!orientationEnabled) return;
      targetX = clamp((event.gamma ?? 0) / 60);
      targetY = clamp((event.beta ?? 0) / 90);
      scheduleRender();
    };
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerleave', resetPointer);
    window.addEventListener('deviceorientation', onOrientation);

    return () => {
      cancelAnimationFrame(frame);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerleave', resetPointer);
      window.removeEventListener('deviceorientation', onOrientation);
      element.style.removeProperty('--px');
      element.style.removeProperty('--py');
    };
  }, []);

  return ref;
}
